import React, { useEffect, useState, useRef, useCallback } from "react";
import { format } from "date-fns";
import { useReactToPrint } from "react-to-print";
import { FaColumns, FaFilter, FaShareAlt } from "react-icons/fa";
import { handleCopyHtmlTable } from "./AddtableShare";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdOutlineFileDownload } from "react-icons/md";
import AddChart from "./ChartAdd";
import CategoryReporting from "./CategoryReporting";
import CampaignTable from "./AddTable/CampaignType";
import AddTable from "./AddTable";
import {
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import axios from "axios";
import {
  apiRequestBodyBuilder,
  getLocalCurrencyStringType,
} from "./config/tableHelper";
import ModifyColumns from "./Tools/ModifyColumns";
import { RenderTable } from "./config/tableWrapper";
import InsightSection from "../components/InsightOverAllComp";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getPreviousDays() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const previousMonthStart = new Date(currentYear, currentMonthIndex - 1, 1);
  const previousMonthEnd = new Date(currentYear, currentMonthIndex, 0);
  const monthBeforePrevStart = new Date(currentYear, currentMonthIndex - 2, 1);
  const monthBeforePrevEnd = new Date(currentYear, currentMonthIndex - 1, 0);
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return {
    curr_start_date: formatDate(previousMonthStart),
    curr_end_date: formatDate(previousMonthEnd),
    prev_start_date: formatDate(monthBeforePrevStart),
    prev_end_date: formatDate(monthBeforePrevEnd),
  };
}

const getDateNames = (day1, day2) => {
  let dayShortObj = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };
  return `${dayShortObj[day1]} - ${dayShortObj[day2]}`;
};

export default function GoogleAdsMonthlyReporting() {
  const previousDates = getPreviousDays();
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const initialDateRange_const = [
    { startDate: firstDayOfMonth, endDate: today, key: "selection" },
    {
      startDate: previousDates.prev_start_date,
      endDate: previousDates.prev_end_date,
      key: "compare",
    },
  ];
  const categoryInsightUrl_const =
    "https://api.confidanto.com/gemini-insights/category-monthly-insights";
  const [categoryInsightUrl, setCategoryInsightUrl] = useState(
    categoryInsightUrl_const,
  );
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [campaignNameInput, setCampaignNameInput] = useState("");
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [insight, setInsight] = useState("");
  const [currentGroupType, setCurrencyGroupType] = useState("");
  const [data, setData] = useState([]);

  const [isEditingInsight, setIsEditingInsight] = useState(false);

  // Buffer timeout of 5500ms to allow child tables and charts to load completely
  useEffect(() => {
    let timer;
    if (!loading) {
      timer = setTimeout(() => {
        setIsReady(true);
      }, 5500);
    } else {
      setIsReady(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  const filterButtonClick = () => {
    const newFilters = [];
    if (selectedFilter === "campaign" && campaignNameInput.trim() !== "") {
      newFilters.push({
        type: "campaign",
        label: `Campaign: ${campaignNameInput}`,
        value: campaignNameInput,
      });
    }
    if (selectedFilter === "group" && selectedGroups.length > 0) {
      selectedGroups.forEach((group) => {
        newFilters.push({
          type: "groupwise",
          label: `Group: ${group}`,
          value: group,
        });
      });
    }
    setAppliedFilters(newFilters);
    fetchReportingData();
    setShowFilterDropdown(false);
    setSelectedFilter(null);
    setCampaignNameInput("");
    setSelectedGroups([]);
  };

  useEffect(() => {
    fetchGroups();
    fetchBudgetColumns();
    fetchReportingData();
  }, []);

  const fetchBudgetColumns = async () => {
    axios
      .post("https://api.confidanto.com/monthly-budget/get", {
        projectId: localStorage.getItem("project_id"),
        customer_id: localStorage.getItem("customer_id"),
        email: localStorage.getItem("email"),
        start_date: previousDates.curr_start_date,
        end_date: previousDates.curr_end_date,
      })
      .then((res) => {
        const structuredColumns = res?.data?.map((item) => ({
          title: item.budget_name,
          id: item.budget_name,
          key: item.budget_name,
          visible: false,
          category: "Budget Name",
          isLocked: false,
        }));
        setColumns((prevColumns) => {
          const insertionIndex = prevColumns.findIndex(
            (col) => col.key === "period_type",
          );
          if (insertionIndex === -1) {
            console.warn(
              "Could not find column with key 'period_type'. Appending new columns to the end.",
            );
            return [...prevColumns, ...structuredColumns];
          }
          const newColumns = [
            ...prevColumns.slice(0, insertionIndex + 1),
            ...structuredColumns,
            ...prevColumns.slice(insertionIndex + 1),
          ];
          return newColumns;
        });
      })
      .catch((err) => console.error("fetchBudgetColumns Error", err));
  };

  const fetchGroups = useCallback(async () => {
    try {
      let requestBody = apiRequestBodyBuilder();
      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      if (!responseInfo.ok) {
        throw new Error(
          `Failed to fetch group info. Status: ${responseInfo.status}`,
        );
      }
      const groupType = await responseInfo.json();
      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");
      const TYPEQUERY =
        typeForQuery?.charAt(0).toUpperCase() + typeForQuery?.slice(1);
      setCurrencyGroupType(TYPEQUERY);
      requestBody.type = typeForQuery;
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch groups. Status: ${response.status}`);
      }
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      console.error("An error occurred while fetching groups:", err);
      setGroups([]);
    }
  }, []);

  const getCurrencySymbol = () => {
    if (
      [7948821642, 1176825945].indexOf(
        Number(localStorage.getItem("customer_id")),
      ) !== -1
    ) {
      return "$";
    }
    return "₹";
  };

  const [currencySymbol] = useState(getCurrencySymbol());
  const [monthlyHeader, setMonthlyHeader] = useState("");
  const [columns, setColumns] = useState([
    {
      id: "1",
      title: "Month",
      key: "period_type",
      visible: true,
      category: "Recommended",
      isLocked: true,
      type: "string",
    },
    {
      id: "2",
      title: "Start Date",
      key: "start_date",
      visible: false,
      category: "Attributes",
      isLocked: false,
      type: "string",
    },
    {
      id: "3",
      title: "End Date",
      key: "end_date",
      visible: false,
      category: "Attributes",
      isLocked: false,
      type: "string",
    },
    {
      id: "4",
      title: "Impr.",
      key: "impressions",
      visible: true,
      category: "Performance",
      isLocked: false,
      type: "number",
    },
    {
      id: "5",
      title: "Clicks",
      key: "clicks",
      visible: true,
      category: "Performance",
      isLocked: false,
      type: "number",
    },
    {
      id: "6",
      title: "CTR",
      key: "ctr",
      visible: true,
      category: "Performance",
      isLocked: false,
      type: "float",
    },
    {
      id: "7",
      title: "Cost",
      key: "cost",
      visible: true,
      category: "Performance",
      isLocked: false,
      type: "currency",
    },
    {
      id: "80",
      title: "Avg. CPM",
      key: "average_cpm",
      visible: false,
      category: "Performance",
      isLocked: false,
      type: "currency",
    },
    {
      id: "81",
      title: "Avg. Cost",
      key: "average_cost",
      visible: false,
      category: "Performance",
      isLocked: false,
      type: "currency",
    },
    {
      id: "8",
      title: "Avg. CPC",
      key: "average_cpc",
      visible: true,
      category: "Performance",
      isLocked: false,
      type: "currency",
    },
    {
      id: "9",
      title: "Conversions",
      key: "conversions",
      visible: true,
      category: "Conversions",
      isLocked: false,
      type: "float-no-perc",
    },
    {
      id: "10",
      title: "Conv. Rate",
      key: "conversion_rate",
      visible: false,
      category: "Conversions",
      isLocked: false,
      type: "float",
    },
    {
      id: "11",
      title: "Cost / Conv",
      key: "cost_per_conversion",
      visible: false,
      category: "Conversions",
      isLocked: false,
      type: "currency",
    },
  ]);

  const categoryRef = useRef(null);
  const componentRef = useRef(null);

  const handlePrintPdf = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Monthly_Reporting_${format(new Date(), "yyyy-MM-dd")}`,
    pageStyle: `
      @media print {
        body { -webkit-print-color-adjust: exact; }
        @page { size: landscape; margin: 8mm; }
        html { zoom: 0.9; }
      }
    `,
    onBeforeGetContent: async () => {
      setDownloadLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
    },
    onAfterPrint: () => setDownloadLoading(false),
    onPrintError: () => setDownloadLoading(false),
  });

  const handleDownloadClick = () => {
    if (
      downloadLoading ||
      shareLoading ||
      loading ||
      !isReady ||
      isEditingInsight
    ) {
      return;
    }
    handlePrintPdf();
  };

  const fetchReportingData = async (remainingGroups = null) => {
    const formatRangeWithOrdinal = (startDateString, endDateString) => {
      function addOrdinalSuffix(day) {
        if (day > 3 && day < 21) return day + "th";
        switch (day % 10) {
          case 1:
            return day + "st";
          case 2:
            return day + "nd";
          case 3:
            return day + "rd";
          default:
            return day + "th";
        }
      }
      if (!startDateString || !endDateString) return "";
      try {
        const startDate = new Date(startDateString + "T00:00:00");
        const endDate = new Date(endDateString + "T00:00:00");
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();
        const startMonth = startDate.toLocaleString(
          getLocalCurrencyStringType(),
          { month: "short" },
        );
        const endMonth = endDate.toLocaleString(getLocalCurrencyStringType(), {
          month: "short",
        });
        const startDayWithSuffix = addOrdinalSuffix(startDay);
        const endDayWithSuffix = addOrdinalSuffix(endDay);
        if (startMonth === endMonth) {
          return `${startDayWithSuffix} - ${endDayWithSuffix} ${endMonth}`;
        } else {
          return `${startDayWithSuffix} ${startMonth} - ${endDayWithSuffix} ${endMonth}`;
        }
      } catch (e) {
        console.error(
          "Error parsing/formatting dates:",
          startDateString,
          endDateString,
          e,
        );
        return "";
      }
    };

    function flattenApiResponse(response) {
      const flattened = [];
      if (response[0] && response[0].totals) {
        let periodType = "";
        if (response[0].start_date === response[0].end_date) {
          periodType = previousDates.curr_start_date;
        } else {
          periodType = formatRangeWithOrdinal(
            previousDates.curr_start_date,
            previousDates.curr_end_date,
          );
        }
        flattened.push({
          period_type: periodType,
          dow_start: getDateNames(response[0].dow_start, response[0].dow_end),
          dow_end: response[0].dow_end,
          start_date: response[0].start_date,
          end_date: response[0].end_date,
          ...response[0].totals,
        });
      }
      if (response[1] && response[1].totals) {
        let periodType = "";
        if (response[0].start_date === response[0].end_date) {
          periodType = previousDates.prev_start_date;
        } else {
          periodType = formatRangeWithOrdinal(
            previousDates.prev_start_date,
            previousDates.prev_end_date,
          );
        }
        flattened.push({
          period_type: periodType,
          dow_start: getDateNames(response[1].dow_start, response[1].dow_end),
          dow_end: response[1].dow_end,
          start_date: response[1].start_date,
          end_date: response[1].end_date,
          ...response[1].totals,
        });
      }
      if (response[2] && response[2].percentage_difference) {
        flattened.push({
          dow_start: "Difference",
          is_difference: true,
          dow_end: "",
          period_type: "Difference",
          start_date: "",
          end_date: "",
          ...response[2].percentage_difference,
        });
      }
      return flattened;
    }

    let url = "https://api.confidanto.com/campaigns/campare-campaigns";
    let requestBody = {
      customer_id: localStorage.getItem("customer_id"),
      email: localStorage.getItem("email"),
      project_id: localStorage.getItem("project_id"),
      ad_account_id: localStorage.getItem("ad_account_id"),
      platform: localStorage.getItem("platform"),
      date_type: "monthly",
      start_date: previousDates.curr_start_date,
      end_date: previousDates.curr_end_date,
      previous_start_date: previousDates.prev_start_date,
      previous_end_date: previousDates.prev_end_date,
    };
    let selectedIds = groups
      .filter((group) => selectedGroups.includes(group.group_name))
      .map((group) => group.group_id);
    if (remainingGroups) {
      selectedIds = groups
        .filter((group) => remainingGroups.includes(group.group_name))
        .map((group) => group.group_id);
    }
    if (selectedIds.length > 0) {
      url = "https://api.confidanto.com/campaign-group/campare";
    }
    let filterBody = {
      group_ids: selectedIds,
      name_contains: campaignNameInput,
    };
    if (selectedFilter != null) {
      filterBody = { ...filterBody, filter_by: selectedFilter };
    }
    setLoading(true);
    setIsReady(false);
    await axios
      .post(url, { ...requestBody, ...filterBody })
      .then((res) => {
        let flattenResponse = flattenApiResponse(res.data);
        setData(flattenResponse);
        setMonthlyHeader(
          formatDateRange(
            previousDates.curr_start_date,
            previousDates.curr_end_date,
          ),
        );
        console.info(
          "Filter Data: ",
          res.data,
          " Flatten Res: ",
          flattenResponse,
        );
      })
      .catch((err) => {
        console.error("fetchReportingData Error: ", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRemoveFilter = async (filterToRemove) => {
    const newFilters = appliedFilters.filter(
      (f) =>
        !(f.type === filterToRemove.type && f.value === filterToRemove.value),
    );
    setAppliedFilters(newFilters);
    const remainingGroups = newFilters
      .filter((f) => f.type === "groupwise")
      .map((f) => f.value);
    const remainingCampaign = newFilters.find((f) => f.type === "campaign");
    if (!remainingCampaign) {
      setCampaignNameInput("");
    }
    if (remainingGroups.length === 0 && !remainingCampaign) {
      setSelectedFilter(null);
    }
    await fetchReportingData(remainingGroups);
  };

  const formatDateRange = (start, end) => {
    try {
      const options = { day: "numeric", month: "short" };
      return `${new Date(start).toLocaleDateString("en-GB", options)} - ${new Date(end).toLocaleDateString("en-GB", options)}`;
    } catch {
      return "Invalid Date";
    }
  };

  const columnRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setShowFilterDropdown(false);
        setSelectedFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActionsDisabled =
    downloadLoading || shareLoading || loading || !isReady || isEditingInsight;

  return (
    <div className="font-roboto text-[#202124] rounded-xl overflow-auto p-4 sm:p-6 mt-6 shadow-sm">
      <div className="w-full flex items-start justify-between mb-10">
        <div>
          <h1 className="text-2xl mt-2 text-gray-600 font-semibold">
            Month: {monthlyHeader}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Download Button */}
          <button
            disabled={isActionsDisabled}
            className={`px-4 py-2 rounded transition-colors duration-200 ${
              isActionsDisabled
                ? "text-gray-400 opacity-60 cursor-not-allowed"
                : "text-gray-600 hover:bg-slate-100"
            }`}
            onClick={handleDownloadClick}
          >
            {downloadLoading ? (
              <div className="flex items-center flex-col justify-center">
                <AiOutlineLoading3Quarters className="animate-spin text-2xl" />
                <span className="ml-2">Processing</span>
              </div>
            ) : !isReady || loading ? (
              <div className="flex items-center flex-col justify-center">
                <AiOutlineLoading3Quarters className="animate-spin text-2xl" />
                <span className="ml-2">Loading...</span>
              </div>
            ) : (
              <div className="flex items-center flex-col justify-center">
                <MdOutlineFileDownload className="text-2xl" />
                <span className="ml-2">Download</span>
              </div>
            )}
          </button>

          {/* Share Button */}
          <button
            disabled={isActionsDisabled}
            className={`px-4 py-2 rounded transition-colors duration-200 ${
              isActionsDisabled
                ? "text-gray-400 opacity-60 cursor-not-allowed"
                : "text-gray-500 hover:bg-slate-100"
            }`}
            onClick={async () => {
              if (isActionsDisabled) return;
              try {
                await handleCopyHtmlTable({
                  previousDates,
                  data,
                  columns,
                  currencySymbol,
                  categoryRef,
                  categoryInsightUrl,
                  setCategoryInsightUrl,
                  insight,
                  setLoading: setShareLoading,
                  PageType: "monthly_reporting",
                });
              } catch (error) {
                console.error("Error in handleCopyHtmlTable:", error);
                alert("Failed to copy report. Please try again.");
              }
            }}
          >
            {shareLoading ? (
              <div className="flex flex-col justify-center items-center">
                <AiOutlineLoading3Quarters className="animate-spin ml-2 text-lg" />
                <span>Sharing...</span>
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center">
                <FaShareAlt className="ml-2 text-lg" />
                <span>Share</span>
              </div>
            )}
          </button>
        </div>
      </div>
      <div
        className="bg-white p-4 rounded-lg print:m-0 print:p-4"
        ref={componentRef}
      >
        <div className="mt-4 bg-white relative">
          <div className="bg-white rounded-md mb-8">
            <div className="flex flex-row justify-start items-center w-full mb-2">
              <h2 className="text-2xl font-semibold text-blue-600 flex flex-col justify-center items-center">
                Overall
              </h2>
            </div>
            <div className="bg-gray-100 font-roboto p-6 print:bg-white print:p-0">
              <div className="flex w-full flex-row-reverse items-center justify-between print:hidden">
                <div className="relative" ref={columnRef}>
                  <button
                    className="bg-transparent text-gray-600 px-4 py-2 rounded items-center hover:bg-slate-100"
                    onClick={() => {
                      setShowColumnsMenu(!showColumnsMenu);
                    }}
                  >
                    <FaColumns className="ml-5" /> Columns
                  </button>
                  {showColumnsMenu && (
                    <ModifyColumns
                      columns={columns}
                      setColumns={setColumns}
                      setShowColumnsMenu={setShowColumnsMenu}
                      colsLimit={13}
                    />
                  )}
                </div>
                <div className="relative" ref={filterRef}>
                  <div className="flex flex-row justify-center items-center">
                    <button
                      className="flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-gray-700 text-sm font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => {
                        setShowFilterDropdown(!showFilterDropdown);
                      }}
                    >
                      <FaFilter className="mr-2" />
                      Filter
                    </button>
                    <div className="flex items-center gap-2 flex-wrap mt-1 ml-2">
                      {appliedFilters.map((filter, index) => (
                        <span
                          key={index}
                          className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-sm flex items-center"
                        >
                          {filter.label}
                          <button
                            className="ml-2 text-blue-600 hover:text-blue-800"
                            onClick={() => handleRemoveFilter(filter)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                  {showFilterDropdown && (
                    <div className="absolute left-0 mt-2 bg-white border p-3 shadow z-20 w-64">
                      {!selectedFilter ? (
                        <div>
                          <button
                            className="block w-full text-left px-2 py-1 hover:bg-gray-100"
                            onClick={() => setSelectedFilter("campaign")}
                          >
                            Campaign Name
                          </button>
                          <button
                            className="block w-full text-left px-2 py-1 hover:bg-gray-100"
                            onClick={() => setSelectedFilter("group")}
                          >
                            {" "}
                            {currentGroupType} Group
                          </button>
                        </div>
                      ) : selectedFilter === "campaign" ? (
                        <div>
                          <label className="block text-sm font-medium mb-1 text-gray-600">
                            Campaign Name
                          </label>
                          <input
                            type="text"
                            value={campaignNameInput}
                            onChange={(e) =>
                              setCampaignNameInput(e.target.value)
                            }
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="Enter campaign name"
                          />
                          <div className="flex justify-end space-x-2 mt-2">
                            <button
                              onClick={filterButtonClick}
                              className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                            >
                              Apply
                            </button>
                            <button
                              onClick={() => {
                                setSelectedFilter(null);
                                setCampaignNameInput("");
                              }}
                              className="text-blue-500 px-3 py-1 rounded text-sm"
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      ) : selectedFilter === "group" ? (
                        <div style={{ marginTop: "16px" }}>
                          <FormControl fullWidth>
                            <InputLabel>{currentGroupType} Group</InputLabel>
                            <Select
                              multiple
                              value={selectedGroups}
                              onChange={(e) => {
                                setSelectedGroups(e.target.value);
                              }}
                              input={
                                <OutlinedInput
                                  label={currentGroupType + " Group"}
                                />
                              }
                              renderValue={(selected) => selected.join(", ")}
                              MenuProps={{ className: "groupwise-select-menu" }}
                            >
                              {groups.map((group) => (
                                <MenuItem
                                  key={group.group_id}
                                  value={group.group_name}
                                >
                                  <Checkbox
                                    checked={selectedGroups.includes(
                                      group.group_name,
                                    )}
                                  />
                                  <ListItemText primary={group.group_name} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <div className="flex flex-row w-full justify-end items-center space-x-2">
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={filterButtonClick}
                              style={{ marginTop: "8px" }}
                            >
                              Apply
                            </Button>
                            <Button
                              onClick={() => {
                                setSelectedFilter(null);
                                setCampaignNameInput("");
                              }}
                              style={{ marginTop: "8px" }}
                              className="bg-white hover:bg-gray-100 mt-2"
                              sx={{
                                color: (theme) => theme.palette.primary.main,
                              }}
                            >
                              Back
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto bg-white rounded shadow mt-2 print:shadow-none">
                {loading ? (
                  <TableSkeleton />
                ) : data.length <= 0 ? (
                  <div className="flex justify-center items-center w-full h-44 text-gray-500 font-medium">
                    No Data Found
                  </div>
                ) : (
                  <RenderTable
                    columns={columns}
                    data={data}
                    variant="reporting"
                    hideTotal={true}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <InsightSection
          insightType="monthly"
          apiInsightType="Monthly"
          onInsightChange={setInsight}
          onEditingChange={setIsEditingInsight}
        />

        <div className="flex flex-col print:break-inside-avoid">
          <CategoryReporting
            previousDates={previousDates}
            categoryInsightUrl={categoryInsightUrl_const}
            categoryType={"Monthly"}
          />
        </div>
        <div className="flex flex-col print:break-inside-avoid">
          <AddTable
            startDate={previousDates.curr_start_date}
            endDate={previousDates.curr_end_date}
            email={localStorage.getItem("email")}
            customerId={localStorage.getItem("customer_id")}
            initialDateRange={initialDateRange_const}
            CampaignTable={CampaignTable}
            isEmpty={true}
            PageType={"monthly_reporting"}
          />
        </div>
        <div className="flex flex-col mb-20 print:break-inside-avoid print:mb-0">
          <AddChart
            previousDates={{
              curr_start_date: previousDates.curr_start_date,
              curr_end_date: previousDates.curr_end_date,
              prev_start_date: previousDates.prev_start_date,
              prev_end_date: previousDates.prev_end_date,
            }}
            PageType={"monthly_reporting"}
          />
        </div>
      </div>
    </div>
  );
}
